import { useCallback, useContext, useEffect } from "react";
import { useBoard } from "../../../providers/BoardDetailProvider";
import { useWebSocketContext } from "../../../providers/WebsocketProvider";
import { LoginUserContext } from "../../../providers/LoginUserProvider";
import {
  COMMENT_PUBLIC,
  ADD_COMMENT,
  ADD_REPLY_COMMENT,
  EDIT_COMMENT,
  EDIT_REPLY_COMMENT,
  DELETE_COMMENT,
  DELETE_REPLY_COMMENT,
  UPDATE_PINNED_COMMENT_STATUS,
  UPDATE_LIKED_COMMENT_STATUS,
  REPORT_COMMENT,
  REPORT_REPLY_COMMENT,
  EVENT_SOURCE_WEB_SOCKET,
  UPDATE_LIKED_BOARD_STATUS,
} from "../../../utils/boardDetailConstants";
import { useGetTabUUID } from "./hooks";

export const useWebsocket = () => {
  const {
    addComment,
    addReplyComment,
    editComment,
    editReplyComment,
    deleteComment,
    deleteReplyComment,
    reportComment,
    reportReplyComment,
    updatePinnedCommentStatus,
    updateLikedCommentStatus,
    updateLikedBoardStatus
  } = useBoard();
  let deviceId = useGetTabUUID();
  const { ws, sendMessage } = useWebSocketContext();
  const currentUser = useContext(LoginUserContext);
  const { uid } = currentUser?.loginUser ?? {};

  useEffect(() => {
    if (!ws) return;
    const handleMessage = (event: MessageEvent) => {
      const newMessage = event?.data ?? "";
      if (newMessage) {
        try {
          const parseNewMessage = JSON.parse(newMessage) ?? {};
          const parseType = parseNewMessage?.type;
          const parseNewMessageData = parseNewMessage?.data;
          const parseDeviceId = parseNewMessageData?.device_id;
          if (deviceId === parseDeviceId) return;

          const { parent_id } = parseNewMessage ?? {};
          switch (parseType) {
            case ADD_COMMENT:
              if (parseNewMessageData.likes_ids) {
                parseNewMessageData.like_flg =
                  parseNewMessageData.likes_ids.includes(uid) ? 1 : 0;
                parseNewMessageData?.children?.forEach((reply: any) => {
                  reply.like_flg = reply.likes_ids.includes(uid) ? 1 : 0;
                });
              }
              const commentFlagsOnAddComment = parseNewMessageData.comment_flags ?? {};
              if (uid  && Object.hasOwn(commentFlagsOnAddComment, uid)) {
                parseNewMessageData.comment_flag = commentFlagsOnAddComment[uid];
              }
              delete parseNewMessageData.comment_flags;
              addComment(parseNewMessageData);
              break;
            case ADD_REPLY_COMMENT:
              const addReplyCommentParentId =
                parent_id || parseNewMessageData?.parent_id;
              if (parseNewMessageData.likes_ids) {
                parseNewMessageData.like_flg =
                  parseNewMessageData.likes_ids.includes(uid) ? 1 : 0;
              }
              const commentFlagsOnAddReplyComment = parseNewMessageData.comment_flags ?? {};
              if (uid && Object.hasOwn(commentFlagsOnAddReplyComment, uid)) {
                parseNewMessageData.comment_flag = commentFlagsOnAddReplyComment[uid];
              }
              delete parseNewMessageData.comment_flags;
              addReplyComment(
                addReplyCommentParentId,
                parseNewMessageData,
                EVENT_SOURCE_WEB_SOCKET
              );
              break;
            case EDIT_COMMENT:
              const newCommentId = parseNewMessageData?.id;
              delete parseNewMessageData.device_id;
              const isWaitedApprovedOrHasNGWords =
                parseNewMessageData?.status !== COMMENT_PUBLIC;
              const commentFlagsOnEditComment = parseNewMessageData.comment_flags ?? {};
              let checkCommentFlagOnEditComment = 0;
              if (uid && Object.hasOwn(commentFlagsOnEditComment, uid)) {
                checkCommentFlagOnEditComment = commentFlagsOnEditComment[uid];
              }
              delete parseNewMessageData.comment_flags;
              if (isWaitedApprovedOrHasNGWords) {
                const countDeletedItems =
                  parseNewMessageData.count_comments_waiting_approve_or_has_ng_words;
                deleteComment(newCommentId, countDeletedItems, checkCommentFlagOnEditComment);
              } else {
                editComment(newCommentId, parseNewMessageData);
              }
              break;
            case EDIT_REPLY_COMMENT:
              const newUpdateReplyCommentId = parseNewMessageData?.id;
              const newUpdateReplyCommentParentId =
                parseNewMessageData.parent_id;
              const isWaitedApprovedOrHasNGWordsReplyComment =
                parseNewMessageData?.status !== COMMENT_PUBLIC;
              const commentFlagsOnEditReplyComment = parseNewMessageData.comment_flags ?? {};
              let checkCommentFlagOnEditReplyComment = 0;
              if (uid && Object.hasOwn(commentFlagsOnEditReplyComment, uid)) {
                checkCommentFlagOnEditReplyComment = commentFlagsOnEditReplyComment[uid];
              }
              delete parseNewMessageData.comment_flags;
              if (isWaitedApprovedOrHasNGWordsReplyComment) {
                deleteReplyComment(
                  newUpdateReplyCommentParentId,
                  newUpdateReplyCommentId,
                  checkCommentFlagOnEditReplyComment
                );
              } else {
                editReplyComment(
                  newUpdateReplyCommentParentId,
                  newUpdateReplyCommentId,
                  parseNewMessageData
                );
              }

              break;
            case DELETE_COMMENT:
              const deletedCommentId = parseNewMessageData.id;
              const totalCommentsDeleted =
                parseNewMessageData.count_deleted_items;
              const commentFlagsOnDeleteComment = parseNewMessageData.comment_flags ?? {};
              let checkCommentFlagOnDeleteComment = 0;
              if (uid && Object.hasOwn(commentFlagsOnDeleteComment, uid)) {
                checkCommentFlagOnDeleteComment = commentFlagsOnDeleteComment[uid];
              }
              deleteComment(deletedCommentId, totalCommentsDeleted, checkCommentFlagOnDeleteComment);
              break;
            case DELETE_REPLY_COMMENT:
              const replyCommentDeletedId = parseNewMessageData.id;
              const replyCommentDeletedParentId = parseNewMessageData.parent_id;
              const commentFlagOnDeleteReplyComment = parseNewMessageData.comment_flags ?? {};
              let checkCommentFlagOnDeleteReplyComment = 0;
              if (uid && Object.hasOwn(commentFlagOnDeleteReplyComment, uid)) {
                checkCommentFlagOnDeleteReplyComment = commentFlagOnDeleteReplyComment[uid];
              }
              deleteReplyComment(
                replyCommentDeletedParentId,
                replyCommentDeletedId, 
                checkCommentFlagOnDeleteReplyComment
              );
              break;
            case REPORT_COMMENT:
              const reportedCommentId = parseNewMessageData?.id;
              const countReportedItems =
                parseNewMessageData?.count_reported_items;
              const commentFlagOnReportComment = parseNewMessageData.comment_flags ?? {};
              let checkCommentFlagOnReportComment = 0;
              if (uid && Object.hasOwn(commentFlagOnReportComment, uid)) {
                checkCommentFlagOnReportComment = commentFlagOnReportComment[uid];
              }
              reportComment(
                reportedCommentId,
                countReportedItems,
                checkCommentFlagOnReportComment,
                EVENT_SOURCE_WEB_SOCKET
              );
              break;
            case REPORT_REPLY_COMMENT:
              const reportedReplyCommentId = parseNewMessageData?.id;
              const reportedReplyCommentParentId =
                parseNewMessageData?.parent_id;
             const commentFlagOnReportReplyComment = parseNewMessageData.comment_flags ?? {};
              let checkCommentFlagOnReportReplyComment = 0;
              if (uid && Object.hasOwn(commentFlagOnReportReplyComment, uid)) {
                checkCommentFlagOnReportReplyComment = commentFlagOnReportReplyComment[uid];
              }
              reportReplyComment(
                reportedReplyCommentParentId,
                reportedReplyCommentId,
                checkCommentFlagOnReportReplyComment,
                EVENT_SOURCE_WEB_SOCKET
              );
              break;
            case UPDATE_PINNED_COMMENT_STATUS:
              const updatedPinnedStatusCommentId = parseNewMessageData.id;
              const pinnedStatus = parseNewMessageData.is_pinned;
              updatePinnedCommentStatus(
                updatedPinnedStatusCommentId,
                pinnedStatus
              );
              break;
            case UPDATE_LIKED_COMMENT_STATUS:
              const likedCommentParentId = parseNewMessageData?.parent_id || 0;
              const likedCommentId = parseNewMessageData.id;
              const likedCommentLikeFlag = parseNewMessageData?.like_flag || 0;
              const targetUserId = parseNewMessageData?.uid;
              updateLikedCommentStatus(
                likedCommentParentId,
                likedCommentId,
                likedCommentLikeFlag,
                uid === targetUserId
              );
              break;
            case UPDATE_LIKED_BOARD_STATUS:
              const likedBoardStatus = parseNewMessageData?.like_flag || 0;
              const actionUserId = parseNewMessageData?.uid;
              updateLikedBoardStatus(likedBoardStatus, actionUserId === uid);
              break;
            default:
              return;
          }
        } catch (error) {
          console.error("Error parsing WebSocket message:", error);
        }
      }
    };

    ws.addEventListener("message", handleMessage);

    return () => {
      ws.removeEventListener("message", handleMessage);
    };
  }, [deviceId, ws]);

  const onSubmit = useCallback(
    (formValue: any) => {
      if (ws?.readyState === WebSocket.OPEN) {
        sendMessage(formValue);
      }
    },
    [sendMessage, ws?.readyState]
  );

  return { onSubmit };
};
